import { useState, useMemo, useCallback } from 'react'
import { CaretDown, CaretRight, Users, Buildings, ChartBar, Newspaper } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { SourcePill } from './SourceBadges'
import {
  FIELD_CATALOG,
  type FieldGroup,
  type FieldGroupDef,
} from './enrichmentFields'

// ---------------------------------------------------------------------------
// Group icons
// ---------------------------------------------------------------------------

export const GROUP_ICONS: Record<FieldGroup, React.ReactNode> = {
  person:    <Users className="w-4 h-4" />,
  company:   <Buildings className="w-4 h-4" />,
  financial: <ChartBar className="w-4 h-4" />,
  news:      <Newspaper className="w-4 h-4" />,
}

// ---------------------------------------------------------------------------
// Component — renders field groups with checkboxes + provider pills
// ---------------------------------------------------------------------------

interface FieldSelectionPanelProps {
  /** Which field groups to show */
  visibleGroups: FieldGroup[]
  /** Controlled: currently selected field IDs */
  selectedFields: Set<string>
  /** Controlled: update selected field IDs */
  onSelectedFieldsChange: (fields: Set<string>) => void
}

export function FieldSelectionPanel({
  visibleGroups,
  selectedFields,
  onSelectedFieldsChange,
}: FieldSelectionPanelProps) {
  const visibleCatalog = useMemo(
    () => FIELD_CATALOG.filter(g => visibleGroups.includes(g.id)),
    [visibleGroups]
  )

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  const toggleField = useCallback((fieldId: string) => {
    const next = new Set(selectedFields)
    if (next.has(fieldId)) next.delete(fieldId)
    else next.add(fieldId)
    onSelectedFieldsChange(next)
  }, [selectedFields, onSelectedFieldsChange])

  const toggleGroup = useCallback((group: FieldGroupDef) => {
    const next = new Set(selectedFields)
    const groupFieldIds = group.fields.map(f => f.id)
    const allSelected = groupFieldIds.every(id => next.has(id))
    if (allSelected) {
      groupFieldIds.forEach(id => next.delete(id))
    } else {
      groupFieldIds.forEach(id => next.add(id))
    }
    onSelectedFieldsChange(next)
  }, [selectedFields, onSelectedFieldsChange])

  const toggleCollapse = useCallback((groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }, [])

  return (
    <div className="space-y-1.5">
      {visibleCatalog.map(group => {
        const groupFieldIds = group.fields.map(f => f.id)
        const selectedInGroup = groupFieldIds.filter(id => selectedFields.has(id)).length
        const allSelected = selectedInGroup === groupFieldIds.length
        const someSelected = selectedInGroup > 0 && !allSelected
        const isCollapsed = collapsedGroups.has(group.id)

        return (
          <div key={group.id} className="rounded-lg border border-border/40 bg-muted/20">
            {/* Group header */}
            <button
              onClick={() => toggleCollapse(group.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 rounded-t-lg transition-colors"
            >
              {isCollapsed
                ? <CaretRight className="w-3.5 h-3.5 text-muted-foreground" />
                : <CaretDown className="w-3.5 h-3.5 text-muted-foreground" />
              }
              <span className="text-muted-foreground">{GROUP_ICONS[group.id]}</span>
              <span className="text-xs font-emphasis text-foreground flex-1">{group.label}</span>
              <span className="text-[10px] text-muted-foreground mr-1">
                {selectedInGroup}/{groupFieldIds.length}
              </span>
              {/* Select All checkbox */}
              <label
                className="flex items-center"
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected }}
                  onChange={() => toggleGroup(group)}
                  className="h-3.5 w-3.5 rounded border-border accent-[#5e6ad2] focus:ring-[#5e6ad2]/40 cursor-pointer"
                />
              </label>
            </button>

            {/* Field rows */}
            {!isCollapsed && (
              <div className="px-2 pb-2 space-y-0.5">
                {group.fields.map(field => {
                  const checked = selectedFields.has(field.id)
                  return (
                    <label
                      key={field.id}
                      className={cn(
                        'flex items-start gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
                        checked
                          ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]'
                          : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleField(field.id)}
                        className="h-3.5 w-3.5 mt-0.5 rounded border-border accent-[#5e6ad2] focus:ring-[#5e6ad2]/40 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-foreground">{field.label}</span>
                        <div className="flex flex-wrap gap-0.5 mt-0.5">
                          {field.providers.map(p => (
                            <SourcePill key={p} provider={p} />
                          ))}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
